// Broadcasts (README scope 6): one approved template to many contacts. Admins and agents prepare a draft (template,
// variables, who gets it, with a live count and a preview); an admin sends the test to their own number, then starts it.
// The queue goes out a few messages at a time within the daily cap (wa-broadcast); the report shows what happened.
import { callFn, rpc, sb } from "../sb.ts";
import { t } from "../i18n.ts";
import { fill, varCount, varsFor, type VarSpec } from "../../supabase/functions/_shared/broadcast.ts";
import { fmtTime, h, mount, toast } from "../ui.ts";
import type { Tpl } from "./templates.ts";

interface B { id: number; line: string; title: string; template_id: number | null; template_name: string; category: string; body: string;
  var_spec: VarSpec; filter: { tags?: string[]; lang?: string | null }; status: string; test_sent_at: string | null; test_sent_by: string | null;
  approved_by: string | null; started_at: string | null; finished_at: string | null; created_by: string | null; created_at: string; updated_at: string }
interface Rep { broadcast_id: number; total: number; queued: number; sent: number; delivered: number; read: number; failed: number; skipped: number; replied: number }

export async function broadcasts(view: HTMLElement, role: string) {
  const isAdmin = role === "admin";
  const box = h("section", { class: "page" });
  const top = h("div", {});
  const listEl = h("div", {});
  mount(view, box);
  mount(box, h("h2", {}, t("Broadcasts")),
    h("p", { class: "muted" }, t("Marketing goes only to contacts who agreed. Numbers that replied STOP never get a broadcast.")),
    h("div", { class: "row end" }, h("button", { class: "primary", onclick: () => form(null) }, "+ " + t("New broadcast"))),
    top, listEl);

  const [{ data: lines }, { data: tpls }] = await Promise.all([
    sb.from("wa_lines").select("line, label").eq("enabled", true).order("created_at"),
    sb.from("wa_templates").select("*").eq("status", "approved").neq("category", "authentication").order("name"),
  ]);
  const usable = (tpls ?? []) as Tpl[];

  async function load() {
    const [{ data, error }, { data: rep }] = await Promise.all([
      sb.from("wa_broadcasts").select("*").order("created_at", { ascending: false }).limit(100),
      sb.from("v_wa_broadcast_report").select("*"),
    ]);
    if (error) return mount(listEl, h("p", { class: "err" }, error.message));
    const reps = new Map(((rep ?? []) as Rep[]).map((r) => [r.broadcast_id, r]));
    const rows = data as B[];
    mount(listEl, rows.length ? h("table", {}, h("tbody", {}, rows.map((b) => {
      const r = reps.get(b.id);
      return h("tr", {},
        h("td", {}, h("b", { dir: "auto" }, b.title), h("div", { class: "muted" }, b.template_name, " · ", t(b.category))),
        h("td", {}, h("span", { class: "pill" + (b.status === "sent" ? " ok" : b.status === "cancelled" ? " bad" : "") }, t(b.status)),
          h("div", { class: "muted" }, fmtTime(b.started_at ?? b.created_at))),
        h("td", {}, r ? counts(r) : h("span", { class: "muted" }, "—")),
        h("td", {}, h("button", { class: "link", onclick: () => (b.status === "draft" ? form(b) : details(b)) }, b.status === "draft" ? t("Open") : t("Report"))));
    }))) : h("p", { class: "muted" }, t("No broadcasts yet")));
  }

  const counts = (r: Rep) => h("small", {}, [[t("To send"), r.queued], [t("Sent"), r.sent], [t("Delivered"), r.delivered], [t("Read"), r.read],
    [t("Replied"), r.replied], [t("Failed"), r.failed], [t("Skipped"), r.skipped]].filter(([, n]) => Number(n) > 0 || r.total === 0)
    .map(([l, n]) => h("span", { class: "tag" }, `${l}: ${n}`)), h("div", { class: "muted" }, t("{n} recipients", { n: r.total })));

  /** a new draft, or a draft to edit / test / start */
  function form(b: B | null) {
    if (!usable.length) {
      mount(top, h("div", { class: "card-in" }, h("p", {}, t("No approved template yet: add one under Templates first.")), h("a", { href: "#templates" }, t("Templates"))));
      return;
    }
    const title = h("input", { value: b?.title ?? "", dir: "auto", placeholder: t("e.g. Autumn offer") });
    const line = h("select", {}, (lines ?? []).map((l) => h("option", { value: l.line, selected: b?.line === l.line, dir: "ltr" }, l.label || "+" + l.line)));
    const tpl = h("select", {}, usable.map((x) => h("option", { value: String(x.id), selected: b?.template_id === x.id }, `${x.name} (${t(x.category)})`)));
    const tags = h("input", { value: (b?.filter?.tags ?? []).join(", "), placeholder: t("empty = every contact") });
    const lang = h("select", {}, [["", t("Any language")], ["ar", "العربية"], ["en", "English"]].map(([v, l]) => h("option", { value: v, selected: (b?.filter?.lang ?? "") === v }, l)));
    const varsBox = h("div", {});
    const count = h("b", {});
    const preview = h("p", { class: "pre", dir: "auto" });
    let spec: VarSpec = { ...(b?.var_spec ?? {}) };
    const chosen = () => usable.find((x) => String(x.id) === tpl.value)!;
    const filter = () => ({ tags: tags.value.split(",").map((x) => x.trim()).filter(Boolean), lang: lang.value || null });

    function drawVars() {
      const n = varCount(chosen().body);
      for (let i = 1; i <= n; i++) spec[i] ??= { from: i === 1 ? "name" : "text", text: "" };
      for (const k of Object.keys(spec)) if (Number(k) > n) delete spec[k];
      mount(varsBox, Array.from({ length: n }, (_, j) => {
        const k = String(j + 1), v = spec[k];
        const from = h("select", { onchange: () => { v.from = from.value as "name" | "text"; drawVars(); } },
          h("option", { value: "name", selected: v.from === "name" }, t("Contact's first name")), h("option", { value: "text", selected: v.from === "text" }, t("Same text for everyone")));
        const text = h("input", { value: v.text, dir: "auto", placeholder: v.from === "name" ? t("if no name, e.g. friend") : t("the text") });
        text.addEventListener("input", () => { v.text = text.value; drawPreview(); });
        return h("div", { class: "row" }, h("label", {}, `{{${k}}}`, from), h("label", {}, v.from === "name" ? t("When there is no name") : t("Text"), text));
      }));
      drawPreview();
    }
    function drawPreview() { preview.textContent = fill(chosen().body, varsFor(spec, "Mona Ali")); }
    let timer: number | undefined;
    async function drawCount() {
      count.textContent = "…";
      try { count.textContent = String(await rpc<number>("audience_count", { p_category: chosen().category, p_filter: filter() })); }
      catch (e) { count.textContent = (e as Error).message; }
    }
    const recount = () => { clearTimeout(timer); timer = window.setTimeout(drawCount, 300); };
    tpl.addEventListener("change", () => { drawVars(); drawCount(); });
    tags.addEventListener("input", recount);
    lang.addEventListener("change", drawCount);

    const save = async (): Promise<number | null> => {
      try {
        const id = await rpc<number>("save_broadcast", { p: { id: b?.id ?? null, title: title.value, line: line.value, template_id: Number(tpl.value), var_spec: spec, filter: filter() } });
        return id;
      } catch (e) { toast((e as Error).message, true); return null; }
    };
    const tested = b?.test_sent_at && b.test_sent_at >= b.updated_at;
    mount(top, h("div", { class: "card-in" },
      h("h3", {}, b ? t("Broadcast draft") : t("New broadcast")),
      h("div", { class: "row" }, h("label", {}, t("Name"), title), h("label", {}, t("From number"), line)),
      h("label", {}, t("Template"), tpl), varsBox,
      h("div", { class: "row" }, h("label", {}, t("Only contacts with these tags"), tags), h("label", {}, t("Language"), lang)),
      h("p", {}, t("Recipients now:") + " ", count),
      h("div", { class: "draft" }, h("small", { class: "muted" }, t("Preview (for a contact named Mona Ali)")), preview),
      b && h("p", { class: "muted" }, tested ? t("Test sent {when} by {who}", { when: fmtTime(b.test_sent_at!), who: b.test_sent_by ?? "" })
        : isAdmin ? t("Next: save, send the test to your own number, then start.") : t("An admin sends the test and starts it.")),
      h("div", { class: "actions" },
        b && h("button", { onclick: async () => {
          if (!confirm(t("Delete this draft?"))) return;
          try { await rpc("delete_broadcast", { p_id: b.id }); mount(top); load(); } catch (e) { toast((e as Error).message, true); }
        } }, t("Delete")),
        h("button", { onclick: () => mount(top) }, t("Close")),
        h("button", { onclick: async () => { const id = await save(); if (id) { toast(t("Saved")); reopen(id); } } }, t("Save draft")),
        b && isAdmin && h("button", { onclick: async (e: Event) => {
          const id = await save(); if (!id) return;
          const btn = e.target as HTMLButtonElement; btn.disabled = true;
          const r = await callFn("wa-broadcast", { mode: "test", id });
          btn.disabled = false;
          if (!r.ok) return toast(r.error ?? "", true);
          toast(t("Test sent to +{to}: check your phone", { to: r.to })); reopen(id);
        } }, t("Send test to me")),
        b && isAdmin && tested && h("button", { class: "primary", onclick: async (e: Event) => {
          const n = await rpc<number>("audience_count", { p_category: b.category, p_filter: b.filter }).catch(() => 0);
          if (!confirm(t("Send “{title}” to {n} contacts now?", { title: b.title, n }))) return;
          const btn = e.target as HTMLButtonElement; btn.disabled = true;
          const r = await callFn("wa-broadcast", { mode: "start", id: b.id });
          btn.disabled = false;
          if (!r.ok) return toast(r.error ?? "", true);
          toast(t("Started: {n} queued", { n: r.queued })); mount(top); load();
        } }, t("Start sending")))));
    drawVars();
    drawCount();
    title.focus();
  }

  async function reopen(id: number) {
    const { data } = await sb.from("wa_broadcasts").select("*").eq("id", id).single();
    await load();
    if (data) form(data as B);
  }

  /** a started broadcast: the counts and every recipient */
  async function details(b: B) {
    const [{ data: r }, { data: sends }] = await Promise.all([
      sb.from("v_wa_broadcast_report").select("*").eq("broadcast_id", b.id).maybeSingle(),
      sb.from("wa_broadcast_sends").select("to_wa_id, name, status, error, sent_at, replied_at").eq("broadcast_id", b.id).order("id").limit(500),
    ]);
    mount(top, h("div", { class: "card-in" },
      h("h3", { dir: "auto" }, b.title),
      h("p", { class: "muted" }, `${b.template_name} · ${t(b.status)} · ${t("started by {who}", { who: b.approved_by ?? "" })}`),
      r && counts(r as Rep),
      h("table", {}, h("tbody", {}, (sends ?? []).map((s: any) => h("tr", {},
        h("td", { dir: "auto" }, s.name || "—"), h("td", { dir: "ltr" }, h("a", { href: `?wa=${s.to_wa_id}#inbox` }, "+" + s.to_wa_id)),
        h("td", {}, t(s.status), s.replied_at && h("span", { class: "tag" }, t("Replied"))), h("td", { class: "muted" }, s.error ?? (s.sent_at ? fmtTime(s.sent_at) : "")))))),
      h("div", { class: "actions" },
        isAdmin && b.status === "sending" && h("button", { onclick: async () => {
          if (!confirm(t("Stop this broadcast? Messages already sent stay sent."))) return;
          try { const n = await rpc<number>("cancel_broadcast", { p_id: b.id }); toast(t("Stopped: {n} not sent", { n })); mount(top); load(); }
          catch (e) { toast((e as Error).message, true); }
        } }, t("Stop")),
        isAdmin && b.status === "sending" && h("button", { onclick: async () => {
          const x = await callFn("wa-broadcast", { mode: "tick" });
          if (!x.ok) return toast(x.error ?? "", true);
          toast(t("Sent {n} more", { n: x.sent })); details(b); load();
        } }, t("Send now")),
        h("button", { onclick: () => mount(top) }, t("Close")))));
  }

  await load();
}
